import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Loader2,
  ArrowUp,
  DollarSign,
  Building2,
} from 'lucide-react';
import { useBudgetDisplay } from '../hooks/useBudgetDisplay';
import { useBudgetFilters } from '../hooks/useBudgetFilters';
import BudgetFilters from '../components/budget/BudgetFilters';
import BudgetTable from '../components/budget/BudgetTable';
import BudgetCards from '../components/budget/BudgetCards';
import DepartmentCardGrid from '../components/budget/DepartmentCardGrid';
import WorkspaceSelectionModal from '../components/WorkspaceSelectionModal';
import { BudgetItem, BudgetFetchParams } from '../types/budget';
import { usePageMetadata } from '../contexts/PageMetadataContext';
import Tabs from '@shared/ui/Tabs';
import ViewToggle from '../components/ViewToggle';
import EmptyState from '../components/EmptyState';

const BudgetPage: React.FC = () => {
  const { setPageMetadata } = usePageMetadata();
  const location = useLocation();

  useEffect(() => {
    setPageMetadata({
      title: 'Budget Items',
      description: 'Search and explore state budget items, departments, and decision items',
    });
    return () => setPageMetadata(null);
  }, [setPageMetadata]);

  const {
    items,
    total,
    isLoading,
    isFetching,
    searchParams,
    showingAll,
    viewMode,
    setViewMode,
    handleSort,
    handleSearch,
    handleFilter,
    handleShowAll,
    handleShowPaginated,
    updateSearchParams,
  } = useBudgetDisplay({ context: 'budget' });

  const { filterOptions, latestFiscalYear } = useBudgetFilters();

  const locationTab = (location.state as { tab?: string } | null)?.tab;
  const [activeTab, setActiveTab] = useState<'items' | 'documents'>(locationTab === 'documents' ? 'documents' : 'items');
  const [searchQuery, setSearchQuery] = useState('');
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BudgetItem | null>(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [fyInitialized, setFyInitialized] = useState(false);

  // Default fiscal year filter to latest available
  useEffect(() => {
    if (!fyInitialized && latestFiscalYear && !searchParams.fiscal_year) {
      handleFilter({ fiscal_year: latestFiscalYear });
      setFyInitialized(true);
    }
  }, [fyInitialized, latestFiscalYear, searchParams.fiscal_year, handleFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // Scroll to top button
  useEffect(() => {
    const handleScroll = () => setShowScrollToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Persist view mode
  useEffect(() => {
    localStorage.setItem('budgetViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    const saved = localStorage.getItem('budgetViewMode');
    if (saved === 'card' || saved === 'table') {
      setViewMode(saved);
    }
  }, [setViewMode]);

  const handleBookmarkClick = useCallback((item: BudgetItem) => {
    setSelectedItem(item);
    setShowBookmarkModal(true);
  }, []);

  const handleBookmarkModalClose = useCallback(() => {
    setShowBookmarkModal(false);
    setSelectedItem(null);
  }, []);

  const handleBookmarkSuccess = useCallback(() => {
    setShowBookmarkModal(false);
    setSelectedItem(null);
  }, []);

  const handleFilterChange = useCallback((filters: Partial<BudgetFetchParams>) => {
    handleFilter(filters);
  }, [handleFilter]);

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    updateSearchParams({
      search: undefined,
      dept_code: undefined,
      fiscal_year: undefined,
      status: undefined,
      fund_type: undefined,
      bill_number: undefined,
      bill_category: undefined,
      has_fte_changes: undefined,
      min_amount: undefined,
      max_amount: undefined,
    });
  }, [updateSearchParams]);

  const tabs = [
    { id: 'items' as const, label: 'Budget Items', icon: DollarSign },
    { id: 'documents' as const, label: 'JBC Departments', icon: Building2 },
  ];

  return (
    <div className="space-y-3 pt-2">
      {/* Tabs */}
      <Tabs
        level="panel"
        ariaLabel="Budget tabs"
        tabs={tabs.map(tab => ({ id: tab.id, label: tab.label, icon: tab.icon }))}
        activeId={activeTab}
        onChange={(id) => setActiveTab(id as typeof activeTab)}
      />

      {activeTab === 'items' && (
        <>
          {/* Filters */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <BudgetFilters
              searchQuery={searchQuery}
              filters={searchParams}
              filterOptions={filterOptions}
              onSearchChange={setSearchQuery}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              variant="full"
            />
          </div>

          {/* View Toggle and Count */}
          <div className="flex justify-between items-center">
            <div className="hidden lg:flex items-center space-x-2">
              <ViewToggle value={viewMode} onChange={setViewMode} />
            </div>

            <div className="flex items-center gap-4">
              {(isLoading || isFetching) && (
                <div className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading...</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  {total.toLocaleString()} budget items found (showing {items.length.toLocaleString()})
                </p>
                {!showingAll && total > items.length && (
                  <button
                    onClick={handleShowAll}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline transition-colors"
                  >
                    Show All
                  </button>
                )}
                {showingAll && (
                  <button
                    onClick={handleShowPaginated}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline transition-colors"
                  >
                    Show Paginated
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Results */}
          <div>
            {isLoading && items.length === 0 ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : !isLoading && items.length === 0 ? (
              <EmptyState hint="No budget items found matching your criteria." />
            ) : viewMode === 'card' ? (
              <BudgetCards
                items={items}
                context="budget"
                onBookmark={handleBookmarkClick}
              />
            ) : (
              <div className="card p-0 overflow-hidden relative">
                {isLoading && (
                  <div className="absolute inset-0 bg-white/60 dark:bg-background-dark-secondary/60 z-10 flex items-center justify-center rounded-lg">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                  </div>
                )}

                {/* Mobile: Card View (< 768px) */}
                <div className="md:hidden">
                  <BudgetCards
                    items={items}
                    context="budget"
                    onBookmark={handleBookmarkClick}
                  />
                </div>

                {/* Desktop: Table View (>= 768px) */}
                <div className="hidden md:block">
                  <BudgetTable
                    items={items}
                    context="budget"
                    sortBy={searchParams.sortBy}
                    sortDirection={searchParams.sortDirection}
                    onSort={handleSort}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Bookmark Modal */}
          <WorkspaceSelectionModal
            isOpen={showBookmarkModal}
            onClose={handleBookmarkModalClose}
            entityType="budget_item"
            entityId={String(selectedItem?.id || '')}
            entityLabel={selectedItem?.item_number || selectedItem?.itemNumber || ''}
            onSave={handleBookmarkSuccess}
          />
        </>
      )}

      {activeTab === 'documents' && (
        <DepartmentCardGrid />
      )}

      {/* Scroll to Top */}
      {showScrollToTop && (
        <>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="lg:hidden fixed bottom-20 right-4 z-50 bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="hidden lg:block fixed bottom-6 right-6 z-50 bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );
};

export default BudgetPage;
